import { Permission } from '@bookora/shared';

import { tabFeature } from '@/features/plan/section-features';

import { joinFa, quoted } from './text';
import type { StepContext, TourDef, TourStepDef } from './types';

/**
 * The CRM tabs (`crm-page.tsx`), each with the key that draws it. Segments
 * are gated on `crm.view_leads` — the key `GET /segments` requires — and the
 * surveys tab on `patient.view`.
 */
const TABS = [
  { id: 'leads', label: 'درخواست‌های مشاوره', key: Permission.CRM_VIEW_LEADS },
  { id: 'segments', label: 'گروه‌های بیماران', key: Permission.CRM_VIEW_LEADS },
  { id: 'campaigns', label: 'پیامک گروهی', key: Permission.CRM_SEND_CAMPAIGNS },
  { id: 'loyalty', label: 'باشگاه مشتریان', key: Permission.LOYALTY_VIEW },
  { id: 'surveys', label: 'رضایت بیماران', key: Permission.PATIENT_VIEW },
] as const;

type TabId = (typeof TABS)[number]['id'];

const BODY: Readonly<Record<TabId, string>> = {
  leads:
    'تماس‌ها و درخواست‌های مشاوره رو ثبت کنید تا بتونید پیگیریشون کنید. وقتی متقاضی آمادهٔ دریافت خدمات شد، براش پرونده بیمار بسازید.',
  // The rolling "has not come for N days" filter exists (lastVisitOlderThanDays).
  segments:
    'گروه شرطی یعنی فهرستی که اعضای آن بر اساس شرایط مشخص، به‌روز می‌شن، مثل «خانم‌هایی که شش ماهه مراجعه نکرده‌ان». هر بار که پیامک می‌فرستید، اعضای گروه دوباره مشخص می‌شن.',
  campaigns:
    'برای یک گروه پیامک بفرستید. قبل از ارسال، تعداد گیرنده‌ها و هزینه رو می‌بینید و تا تأیید نکنید چیزی فرستاده نمی‌شه.',
  loyalty:
    'امتیاز خرید و معرفی دوستان؛ بیماران می‌تونن از امتیازهاشون در جلسه‌های درمان بعدی استفاده کنن.',
  surveys:
    'نمره و نظر بیماران بعد از جلسه درمان اینجا جمع می‌شه. پاسخ‌های با امتیاز پایین جدا نشون داده می‌شن تا پیگیریشون کنید.',
};

/**
 * The phone's one-tab step. The spotlight is the whole strip, so each body
 * names its tab and never says «اینجا»; written whole rather than prefixed
 * onto `BODY`, which would leave «در زبانهٔ … گروه یعنی…» and a fragment for
 * the club.
 */
const COMPACT_BODY: Readonly<Record<Exclude<TabId, 'leads'>, string>> = {
  segments:
    'در تب «گروه‌های بیماران» گروه‌های شرطی می‌سازید که اعضاشون موقع ارسال پیامک دوباره بررسی می‌شن، مثل «خانم‌هایی که شش ماهه مراجعه نکرده‌ان». هر بار که پیامک می‌فرستید، اعضای گروه دوباره مشخص می‌شن.',
  campaigns:
    'در تب «پیامک گروهی» برای یک گروه پیامک بفرستید. قبل از ارسال، تعداد گیرنده‌ها و هزینه رو می‌بینید و تا تأیید نکنید چیزی فرستاده نمی‌شه.',
  loyalty:
    'در تب «باشگاه مشتریان» امتیاز خرید و معرفی دوستان جمع می‌شه؛ بیماران می‌تونن از امتیازهاشون در جلسه‌های درمان بعدی استفاده کنن.',
  surveys:
    'در تب «رضایت بیماران» نمره و نظر بیماران بعد از جلسه درمان جمع می‌شه. پاسخ‌های با امتیاز پایین جدا نشون داده می‌شن تا پیگیریشون کنید.',
};

function heldTabs(ctx: StepContext): (typeof TABS)[number][] {
  return TABS.filter((tab) => ctx.can(tab.key));
}

/**
 * Wide: one step per tab label. Compact: every tab label is behind the same
 * strip (`crm.tabs`), so instead of spotlighting it once per tab the phone
 * gets an overview naming only the viewer's own tabs, then ONE of them in a
 * little more depth (the first the viewer holds, groups first — where the
 * finish button leads).
 */
/**
 * The plan feature behind a tab (owner D6). A tab the clinic's plan locks
 * still shows, with «پلن بالاتر» — but the tour does not walk the viewer into
 * a screen that answers «این بخش در پلن … است»: its step needs the feature ON
 * (`requiresFeature`). The feature comes from the one map.
 */
function planFeatureOf(id: TabId): TourStepDef['requiresFeature'] {
  return tabFeature('crm', id);
}

function wideTabStep(id: TabId, title: string): TourStepDef {
  const tab = TABS.find((t) => t.id === id);
  const feature = planFeatureOf(id);
  return {
    id,
    anchor: { wide: `crm.tab-${id}`, compact: null },
    placement: 'bottom',
    requires: { all: [tab?.key ?? Permission.CRM_VIEW_LEADS] },
    ...(feature ? { requiresFeature: feature } : {}),
    title,
    body: BODY[id],
  };
}

function compactFocusStep(id: Exclude<TabId, 'leads'>): TourStepDef {
  const tab = TABS.find((t) => t.id === id);
  const feature = planFeatureOf(id);
  return {
    id: `${id}-compact`,
    group: 'compact-focus',
    anchor: { wide: null, compact: 'crm.tabs' },
    placement: 'bottom',
    requires: { all: [tab?.key ?? Permission.CRM_VIEW_LEADS] },
    ...(feature ? { requiresFeature: feature } : {}),
    title: tab?.label ?? '',
    body: COMPACT_BODY[id],
  };
}

/**
 * Version 2: the gate widened from `crm.view_leads` to any CRM tab key (a
 * nurse with the club and surveys tabs had no tour), a surveys step joined,
 * compact collapsed its four same-strip steps, and the groups body lost
 * «فیلترهای ذخیره‌شده». The finish leads to «گروه جدید».
 */
export const CRM_TOUR: TourDef = {
  key: 'crm',
  version: 2,
  surface: 'page',
  title: 'درخواست‌های مشاوره و پیامک',
  hubDescription:
    'درخواست‌های مشاوره، گروه‌های بیماران، پیامک گروهی، باشگاه مشتریان و رضایت بیماران.',
  offer: {
    body: 'در چند مرحله نشونتون می‌دیم چطور درخواست‌های مشاوره و بیمارانی رو که مدتی مراجعه نکرده‌اند پیگیری کنید.',
  },
  route: { path: '/crm', match: (pathname) => pathname === '/crm' },
  requires: {
    any: [Permission.CRM_VIEW_LEADS, Permission.CRM_SEND_CAMPAIGNS, Permission.LOYALTY_VIEW],
  },
  finish: {
    label: 'ساخت گروه جدید',
    // `?tab=segments&new=1` opens the group editor (segments-tab.tsx).
    to: '/crm?tab=segments&new=1',
    requires: { all: [Permission.CRM_VIEW_LEADS, Permission.CRM_MANAGE_LEADS] },
  },
  autoOffer: true,
  maxSteps: 5,
  minSteps: 2,
  steps: [
    {
      id: 'tabs',
      anchor: { wide: null, compact: 'crm.tabs' },
      placement: 'bottom',
      title: 'تب‌های این بخش',
      when: (ctx) => heldTabs(ctx).length > 1,
      body: (ctx) => {
        const tabs = heldTabs(ctx);
        return tabs.length > 1
          ? `این بخش چند تب داره: ${joinFa(tabs.map((t) => quoted(t.label)))}. هرکدوم رو بزنید تا باز بشه.`
          : 'تب‌های این بخش رو از همین نوار باز کنید.';
      },
    },
    wideTabStep('leads', 'درخواست‌های مشاوره'),
    wideTabStep('segments', 'گروه‌های بیماران'),
    wideTabStep('campaigns', 'پیامک گروهی'),
    wideTabStep('loyalty', 'باشگاه مشتریان'),
    wideTabStep('surveys', 'رضایت بیماران'),
    compactFocusStep('segments'),
    compactFocusStep('campaigns'),
    compactFocusStep('loyalty'),
    compactFocusStep('surveys'),
  ],
};
